/**
 * @file authSlice.ts
 * @description Redux Toolkit slice for managing authentication state and access tokens with localStorage persistence.
 */
import type { PublicUserProfile } from '@forex-builder/shared-types';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export interface AuthState {
  user: PublicUserProfile | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
}

const getSavedToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  // 'token' is a key from an older build that nothing writes any more. Reading
  // it back would rehydrate the store with a long-dead credential, so it is
  // cleared rather than trusted.
  localStorage.removeItem('token');
  return localStorage.getItem('accessToken');
};

const getSavedUser = (): PublicUserProfile | null => {
  if (typeof window === 'undefined') return null;
  try {
    const str = localStorage.getItem('user');
    return str ? (JSON.parse(str) as PublicUserProfile) : null;
  } catch {
    return null;
  }
};

const savedToken = getSavedToken();
const savedUser = getSavedUser();

const initialState: AuthState = {
  user: savedUser,
  accessToken: savedToken,
  isAuthenticated: !!savedToken,
  isInitializing: false,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{ user: PublicUserProfile; accessToken: string }>,
    ) => {
      state.user = action.payload.user;
      state.accessToken = action.payload.accessToken;
      state.isAuthenticated = true;
      state.isInitializing = false;

      if (typeof window !== 'undefined') {
        localStorage.setItem('accessToken', action.payload.accessToken);
        localStorage.setItem('user', JSON.stringify(action.payload.user));
      }
    },
    updateUser: (state, action: PayloadAction<PublicUserProfile>) => {
      state.user = action.payload;
      if (typeof window !== 'undefined') {
        localStorage.setItem('user', JSON.stringify(action.payload));
      }
    },
    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.isAuthenticated = false;
      state.isInitializing = false;

      if (typeof window !== 'undefined') {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    },
    setInitialized: (state) => {
      state.isInitializing = false;
    },
  },
});

export const { setCredentials, updateUser, logout, setInitialized } = authSlice.actions;
export default authSlice.reducer;
